import axios from 'axios';
import dayjs from 'dayjs';
import { t } from 'i18next';

import { throwError } from '../../modules/error';
import { companySnapshot } from '../firebase/firestore/company';
import { issueSnapshot } from '../firebase/firestore/issue';
import { mediaSnapshot } from '../firebase/firestore/media';
import { userSnapshot } from '../firebase/firestore/user';

import type { NegotiationType } from '../firebase/firestore/negotiation/negotiationType';
import type {
  DocumentData,
  DocumentSnapshot,
  Timestamp
} from 'firebase/firestore';

const retoolUrlEnv = (env: string): string => {
  if (env === 'stg') {
    return '&_environment=staging';
  } else if (env === 'prd') {
    return '&_environment=production';
  } else if (env === 'dev') {
    return '&_environment=dev';
  }
  return '';
};

const mailList = (): string => {
  if (process.env.REACT_APP_ENV === 'prd') {
    return 'admin@en-broker.co.jp';
  } else if (process.env.REACT_APP_ENV === 'stg') {
    return 'test@en-broker.co.jp';
  } else if (process.env.REACT_APP_ENV === 'dev') {
    return 'dev@hi-story.co.jp';
  }
  return '';
};

const envUrl = retoolUrlEnv(process.env.REACT_APP_ENV ?? '');

const getCompensationsString = (compensation: number[]): string =>
  compensation
    .map((elm) => {
      if (elm === 1) {
        return t('message.order.compensation.1');
      } else if (elm === 2) {
        return t('message.order.compensation.2');
      } else if (elm === 3) {
        return t('message.order.compensation.3');
      } else if (elm === 4) {
        return t('message.order.compensation.4');
      } else if (elm === 5) {
        return t('message.order.compensation.5');
      } else if (elm === 6) {
        return t('message.order.compensation.6');
      }
      return '';
    })
    .join(',');

export const sendMailAfterOrderApprovalClient = async (
  order: DocumentData,
  negotiation: NegotiationType,
  publisherName: string,
  issueName: string,
  clientName: string
  // agentName: string
): Promise<void> => {
  try {
    const headers = {
      'Content-Type': 'application/json'
    };
    const compensation = getCompensationsString(order.compensation as number[]);
    const sendMail = await axios.post(
      `https://us-central1-${
        process.env.REACT_APP_GCP_PROJECT_ID ?? ''
      }.cloudfunctions.net/sendMailAfterOrderApprovalClient`,
      {
        data: {
          // agentName,
          // agentUrl: `https://mediage.retool.com/apps/043a0ffa-4394-11ed-b8d2-df711305a588/%5BMediAge%5DR09_R10_EditDetail_Agent?=&agentId=${negotiation.agentId}${envUrl}`,
          amount: order.amount as number,
          clientName,
          clientUrl: `https://mediage.retool.com/apps/2f6bde98-2340-11ed-bd0a-1771b272afbd/%5BMediAge%5DR02_R03_EditDetail_Client?clientId=${negotiation.clientId}${envUrl}`,
          companyName: order.companyName as string,
          compensation,
          depositTiming: order.depositTiming as string,
          issueName,
          issueUrl: `https://mediage.retool.com/apps/76c0ec6c-3a3c-11ed-b271-97093ce557fd/%5BMediAge%5DR19_EditDetail_Issue?issueId=${negotiation.issueId}${envUrl}`,
          kpi: order.kpi as string,
          mediaUrl: `https://mediage.retool.com/apps/e7f32662-4aad-11ed-b856-035bfad2961c/%5BMediAge%5DR14_R16_EditDetail_Media?mediaId=${negotiation.mediaId}${envUrl}`,
          optionContent: order.optionContent as string,
          optionFlg: String(order.optionFlg) === '1' ? 'あり' : 'なし',
          pkgName: order.pkgName as string,
          publishEndDate: dayjs(
            (order.publishEndDate as Timestamp)?.toDate()
          ).format('YYYY/MM/DD'),
          publishStartDate: dayjs(
            (order.publishStartDate as Timestamp)?.toDate()
          ).format('YYYY/MM/DD'),
          publisherName,
          publisherUrl: `https://mediage.retool.com/apps/63038b38-3df9-11ed-a039-4f461572c243/%5BMediAge%5DR12_R13_EditDetail_Publisher?publisherId=${negotiation.publisherId}${envUrl}`,
          reportFlg: String(order.reportFlg) === '1' ? 'あり' : 'なし',
          reportSubmissionDate: order.reportSubmissionDate
            ? dayjs((order.reportSubmissionDate as Timestamp).toDate()).format(
                'YYYY/MM/DD'
              )
            : 'なし',
          to: mailList(),
          transferTiming:
            String(order.transferTiming) === '1' ? '前払い' : '後払い'
        }
      },
      { headers }
    );
    if (!sendMail) {
      throw new Error();
    }
  } catch (e) {
    throwError(e);
  }
};

export const sendMailAfterOrderApprovalAgent = async (
  order: DocumentData,
  negotiation: NegotiationType,
  publisherName: string,
  issueName: string,
  agentName: string
): Promise<void> => {
  try {
    const headers = {
      'Content-Type': 'application/json'
    };
    const compensation = getCompensationsString(order.compensation as number[]);
    const sendMail = await axios.post(
      `https://us-central1-${
        process.env.REACT_APP_GCP_PROJECT_ID ?? ''
      }.cloudfunctions.net/sendMailAfterOrderApprovalAgent`,
      {
        data: {
          agentName,
          agentUrl: `https://mediage.retool.com/apps/043a0ffa-4394-11ed-b8d2-df711305a588/%5BMediAge%5DR09_R10_EditDetail_Agent?agentId=${negotiation.agentId}${envUrl}`,
          amount: order.amount as number,
          companyName: order.companyName as string,
          compensation,
          depositTiming: order.depositTiming as string,
          issueName,
          issueUrl: `https://mediage.retool.com/apps/76c0ec6c-3a3c-11ed-b271-97093ce557fd/%5BMediAge%5DR19_EditDetail_Issue?issueId=${negotiation.issueId}${envUrl}`,
          kpi: order.kpi as string,
          mediaUrl: `https://mediage.retool.com/apps/e7f32662-4aad-11ed-b856-035bfad2961c/%5BMediAge%5DR14_R16_EditDetail_Media?mediaId=${negotiation.mediaId}${envUrl}`,
          optionContent: order.optionContent as string,
          optionFlg: String(order.optionFlg) === '1' ? 'あり' : 'なし',
          pkgName: order.pkgName as string,
          publishEndDate: dayjs(
            (order.publishEndDate as Timestamp)?.toDate()
          ).format('YYYY/MM/DD'),
          publishStartDate: dayjs(
            (order.publishStartDate as Timestamp)?.toDate()
          ).format('YYYY/MM/DD'),
          publisherName,
          publisherUrl: `https://mediage.retool.com/apps/63038b38-3df9-11ed-a039-4f461572c243/%5BMediAge%5DR12_R13_EditDetail_Publisher?publisherId=${negotiation.publisherId}${envUrl}`,
          reportFlg: String(order.reportFlg) === '1' ? 'あり' : 'なし',
          reportSubmissionDate: order.reportSubmissionDate
            ? dayjs((order.reportSubmissionDate as Timestamp).toDate()).format(
                'YYYY/MM/DD'
              )
            : 'なし',
          to: mailList(),
          transferTiming:
            String(order.transferTiming) === '1' ? '前払い' : '後払い'
        }
      },
      { headers }
    );
    if (!sendMail) {
      throw new Error();
    }
  } catch (e) {
    throwError(e);
  }
};

export const sendMailToClient = async (
  user: DocumentSnapshot<DocumentData>,
  company: DocumentSnapshot<DocumentData>,
  industry: string
): Promise<void> => {
  try {
    if (user.exists() && company.exists()) {
      const headers = {
        'Content-Type': 'application/json'
      };
      const sendMail = await axios.post(
        `https://us-central1-${
          process.env.REACT_APP_GCP_PROJECT_ID ?? ''
        }.cloudfunctions.net/sendMail`,
        {
          data: {
            cName: company.data()?.name as string,
            cUrl: company.data()?.url as string,
            companyId: company.data()?.companyId as string,
            department: user.data()?.department as string,
            email: user.data()?.email as string,
            industry,
            retoolUrl: `https://mediage.retool.com/apps/2f6bde98-2340-11ed-bd0a-1771b272afbd/%5BMediAge%5DR02_R03_EditDetail_Client?clientId=${
              company.data()?.companyId as string
            }${envUrl}`,
            to: mailList(),
            type: '広告主',
            uName: user.data()?.name as string
          }
        },
        { headers }
      );
      if (!sendMail) {
        throw new Error();
      }
    }
  } catch (e) {
    throwError(e);
  }
};

export const sendMailToAgent = async (
  user: DocumentSnapshot<DocumentData>,
  company: DocumentSnapshot<DocumentData>,
  industry: string
): Promise<void> => {
  try {
    if (user.exists() && company.exists()) {
      const headers = {
        'Content-Type': 'application/json'
      };
      const sendMail = await axios.post(
        `https://us-central1-${
          process.env.REACT_APP_GCP_PROJECT_ID ?? ''
        }.cloudfunctions.net/sendMail`,
        {
          data: {
            cName: company.data()?.name as string,
            cUrl: company.data()?.url as string,
            companyId: company.data()?.companyId as string,
            department: user.data()?.department as string,
            email: user.data()?.email as string,
            industry,
            retoolUrl: `https://mediage.retool.com/apps/043a0ffa-4394-11ed-b8d2-df711305a588/%5BMediAge%5DR09_R10_EditDetail_Agent?=&agentId=${
              company.data()?.companyId as string
            }${envUrl}`,
            to: mailList(),
            type: '代理店',
            uName: user.data()?.name as string
          }
        },
        { headers }
      );
      if (!sendMail) {
        throw new Error();
      }
    }
  } catch (e) {
    throwError(e);
  }
};

export const sendMailToPublisher = async (
  user: DocumentSnapshot<DocumentData>,
  company: DocumentSnapshot<DocumentData>,
  industry: string
): Promise<void> => {
  try {
    if (user.exists() && company.exists()) {
      const headers = {
        'Content-Type': 'application/json'
      };
      const sendMail = await axios.post(
        `https://us-central1-${
          process.env.REACT_APP_GCP_PROJECT_ID ?? ''
        }.cloudfunctions.net/sendMail`,
        {
          data: {
            cName: company.data()?.name as string,
            cUrl: company.data()?.url as string,
            companyId: company.data()?.companyId as string,
            email: user.data()?.email as string,
            industry,
            retoolUrl: `https://mediage.retool.com/apps/63038b38-3df9-11ed-a039-4f461572c243/%5BMediAge%5DR12_R13_EditDetail_Publisher?publisherId=${
              company.data()?.companyId as string
            }${envUrl}`,
            to: mailList(),
            type: '媒体社',
            uName: user.data()?.name as string
          }
        },
        { headers }
      );
      if (!sendMail) {
        throw new Error();
      }
    }
  } catch (e) {
    throwError(e);
  }
};

export const sendMailToInquiry = async (
  locationInfo: {
    companyName: string;
    email: string;
    phone?: string;
    category: string;
    message: string;
    contactKind: string;
    contactNumbers: string;
    type: string;
  },
  category: string
): Promise<void> => {
  try {
    const headers = {
      'Content-Type': 'application/json'
    };
    const sendMail = await axios.post(
      `https://us-central1-${
        process.env.REACT_APP_GCP_PROJECT_ID ?? ''
      }.cloudfunctions.net/sendMailToInquiry`,
      {
        data: {
          category,
          email: locationInfo.email,
          message: locationInfo.message,
          name: locationInfo.companyName,
          tel: locationInfo.phone,
          to: mailList()
        }
      },
      { headers }
    );
    if (!sendMail) {
      throw new Error();
    }
  } catch (e) {
    throwError(e);
  }
};

export const sendMailIssueClient = async (
  companySnap: DocumentSnapshot<DocumentData>,
  issueSnap: DocumentSnapshot<DocumentData>,
  typeText: string,
  genre: string,
  purpose: string,
  budget: string,
  agentName: string,
  merchandiseType: string
): Promise<void> => {
  try {
    const headers = {
      'Content-Type': 'application/json'
    };
    let retoolUrl = `https://mediage.retool.com/apps/880a3ede-2442-11ed-950b-53d24c069a9e/%5BMediAge%5DR04_R06_EditDetail_Issue?agentId=${
      issueSnap.data()?.agentId as string
    }&agentName=${encodeURI(agentName)}&clientId=${
      companySnap.data()?.companyId as string
    }&clientName=${encodeURI(companySnap.data()?.name as string)}&issueId=${
      issueSnap.data()?.issueId as string
    }&issueName=${encodeURI(issueSnap.data()?.name as string)}${retoolUrlEnv(
      process.env.REACT_APP_ENV ?? ''
    )}#=`;
    retoolUrl = encodeURI(retoolUrl);

    const budgetText = budget !== '' ? budget : t('consult.label1');

    const sendMail = await axios.post(
      `https://us-central1-${
        process.env.REACT_APP_GCP_PROJECT_ID ?? ''
      }.cloudfunctions.net/sendMailIssueClient`,
      {
        data: {
          applicationDate: `${
            (
              issueSnap.data()?.applicationDate as {
                start: Timestamp;
                end: Timestamp;
              }
            ).start
              ? dayjs(
                  (
                    issueSnap.data()?.applicationDate as {
                      start: Timestamp;
                      end: Timestamp;
                    }
                  ).start.toDate()
                ).format('YYYY/MM/DD')
              : ''
          }〜${
            (
              issueSnap.data()?.applicationDate as {
                start: Timestamp;
                end: Timestamp;
              }
            ).end
              ? dayjs(
                  (
                    issueSnap.data()?.applicationDate as {
                      start: Timestamp;
                      end: Timestamp;
                    }
                  ).end.toDate()
                ).format('YYYY/MM/DD')
              : ''
          }`,
          budget: budgetText,
          companyName: companySnap.data()?.name as string,
          genre,
          issueName: issueSnap.data()?.name as string,
          merchandiseType,
          purpose,
          retoolUrl,
          startDate: issueSnap.data()?.startDate
            ? dayjs((issueSnap.data()?.startDate as Timestamp).toDate()).format(
                'YYYY/MM/DD'
              )
            : t('consult.label1'),
          to: mailList(),
          type: typeText
        }
      },
      { headers }
    );
    if (!sendMail) {
      throw new Error();
    }
  } catch (e) {
    throwError(e);
  }
};

export const sendMailIssueAgent = async (
  companySnap: DocumentSnapshot<DocumentData>,
  issueSnap: DocumentSnapshot<DocumentData>,
  typeText: string,
  genre: string,
  purpose: string,
  budget: string,
  agentName: string,
  merchandiseType: string,
  clientSnapName: string
): Promise<void> => {
  try {
    const headers = {
      'Content-Type': 'application/json'
    };
    let retoolUrl = `https://mediage.retool.com/apps/043a0ffa-4394-11ed-b8d2-df711305a588/%5BMediAge%5DR09_R10_EditDetail_Agent?=&agentId=${
      issueSnap.data()?.agentId as string
    }&_releaseVersion=latest${retoolUrlEnv(process.env.REACT_APP_ENV ?? '')}`;

    retoolUrl = encodeURI(retoolUrl);

    const budgetText = budget !== '' ? budget : t('consult.label1');

    const sendMail = await axios.post(
      `https://us-central1-${
        process.env.REACT_APP_GCP_PROJECT_ID ?? ''
      }.cloudfunctions.net/sendMailIssueAgent`,
      {
        data: {
          agentName,
          applicationDate: `${
            (
              issueSnap.data()?.applicationDate as {
                start: Timestamp;
                end: Timestamp;
              }
            ).start
              ? dayjs(
                  (
                    issueSnap.data()?.applicationDate as {
                      start: Timestamp;
                      end: Timestamp;
                    }
                  ).start.toDate()
                ).format('YYYY/MM/DD')
              : ''
          }〜${
            (
              issueSnap.data()?.applicationDate as {
                start: Timestamp;
                end: Timestamp;
              }
            ).end
              ? dayjs(
                  (
                    issueSnap.data()?.applicationDate as {
                      start: Timestamp;
                      end: Timestamp;
                    }
                  ).end.toDate()
                ).format('YYYY/MM/DD')
              : ''
          }`,
          budget: budgetText,
          clientName: clientSnapName,
          genre,
          issueName: issueSnap.data()?.name as string,
          merchandiseType,
          purpose,
          retoolUrl,
          startDate: issueSnap.data()?.startDate
            ? dayjs((issueSnap.data()?.startDate as Timestamp).toDate()).format(
                'YYYY/MM/DD'
              )
            : t('consult.label1'),
          to: mailList(),
          type: typeText
        }
      },
      { headers }
    );
    if (!sendMail) {
      throw new Error();
    }
  } catch (e) {
    throwError(e);
  }
};

export const sendMailMediaPublisher = async (
  companySnap: DocumentSnapshot<DocumentData>,
  mediaSnap: DocumentSnapshot<DocumentData>,
  genre: string,
  mediaType: string
): Promise<void> => {
  try {
    const headers = {
      'Content-Type': 'application/json'
    };
    let retoolUrl = `https://mediage.retool.com/apps/e7f32662-4aad-11ed-b856-035bfad2961c/%5BMediAge%5DR14_R16_EditDetail_Media?=&mediaId=${
      mediaSnap.data()?.mediaId as string
    }&mediaName=${encodeURI(mediaSnap.data()?.name as string)}&publisherId=${
      companySnap.data()?.companyId as string
    }&publisherName=${encodeURI(
      companySnap.data()?.name as string
    )}&_releaseVersion=latest${retoolUrlEnv(process.env.REACT_APP_ENV ?? '')}`;

    retoolUrl = encodeURI(retoolUrl);

    const sendMail = await axios.post(
      `https://us-central1-${
        process.env.REACT_APP_GCP_PROJECT_ID ?? ''
      }.cloudfunctions.net/sendMailMediaPublisher`,
      {
        data: {
          companyName: companySnap.data()?.name as string,
          genre,
          mediaName: mediaSnap.data()?.name as string,
          mediaType,
          retoolUrl,
          to: mailList()
        }
      },
      { headers }
    );
    if (!sendMail) {
      throw new Error();
    }
  } catch (e) {
    throwError(e);
  }
};

export const sendClientAgentNoticeFromPublisher = async (
  negotiation: NegotiationType,
  email: string,
  action: string,
  message: string,
  senderId: string
): Promise<void> => {
  try {
    const company = await companySnapshot(negotiation?.publisherId ?? '');
    const companyName = (company?.data()?.name as string) ?? '';
    const media = await mediaSnapshot(negotiation?.mediaId ?? '');
    const mediaName = (media?.data()?.name as string) ?? '';
    const mainUser = await userSnapshot(senderId);
    const mainUserName = (mainUser?.data()?.name as string) ?? '';
    const issue = await issueSnapshot(negotiation?.issueId ?? '');
    const issueName = (issue?.data()?.name as string) ?? '';
    const url = negotiation?.agentId
      ? `${window.location.protocol}//${window.location.host}/main/agent/mypage/message/${negotiation?.negotiationId}`
      : `${window.location.protocol}//${window.location.host}/main/client/mypage/message/${negotiation?.negotiationId}`;
    const headers = {
      'Content-Type': 'application/json'
    };

    const htmlText = convertNewLineToBr(message);

    const sendMail = await axios.post(
      `https://us-central1-${
        process.env.REACT_APP_GCP_PROJECT_ID ?? ''
      }.cloudfunctions.net/sendClientAgentNoticeFromPublisher`,
      {
        data: {
          ServiceName: getServiceName('MediAge'),
          action,
          companyName,
          issueName,
          mainUserName,
          mediaName,
          message: htmlText,
          to: getEmail(email),
          url
        }
      },
      { headers }
    );
    if (!sendMail) {
      throw new Error();
    }
  } catch (e) {
    throwError(e);
  }
};

export const sendPublisherNoticeFromClientAgent = async (
  negotiation: NegotiationType,
  email: string,
  action: string,
  message: string,
  senderId: string
): Promise<void> => {
  try {
    const company = negotiation?.agentId
      ? await companySnapshot(negotiation?.agentId ?? '')
      : await companySnapshot(negotiation?.clientId ?? '');
    const companyName = (company?.data()?.name as string) ?? '';
    const media = await mediaSnapshot(negotiation?.mediaId ?? '');
    const mediaName = (media?.data()?.name as string) ?? '';
    const mainUser = await userSnapshot(senderId);
    const mainUserName = (mainUser?.data()?.name as string) ?? '';
    const issue = await issueSnapshot(negotiation?.issueId ?? '');
    const issueName = (issue?.data()?.name as string) ?? '';
    const url = `${window.location.protocol}//${window.location.host}/main/publisher/mypage/message/${negotiation?.negotiationId}`;
    const headers = {
      'Content-Type': 'application/json'
    };

    const htmlText = convertNewLineToBr(message);

    const sendMail = await axios.post(
      `https://us-central1-${
        process.env.REACT_APP_GCP_PROJECT_ID ?? ''
      }.cloudfunctions.net/sendPublisherNoticeFromClientAgent`,
      {
        data: {
          ServiceName: getServiceName('MediAge'),
          action,
          companyName,
          issueName,
          mainUserName,
          mediaName,
          message: htmlText,
          to: getEmail(email),
          url
        }
      },
      { headers }
    );
    if (!sendMail) {
      throw new Error();
    }
  } catch (e) {
    throwError(e);
  }
};

export const sendNoticeMail = (
  mainUid: string,
  member: string[],
  chat: string,
  negotiationSnap: NegotiationType,
  action: string,
  senderId: string
): void => {
  const mUid = mainUid;
  const memberUids = member;
  const idsArray = memberUids;
  idsArray?.push(mUid);
  idsArray?.forEach(async (memberUid) => {
    if (memberUid) {
      const userSnap = await userSnapshot(memberUid);
      await sendClientAgentNoticeFromPublisher(
        negotiationSnap,
        userSnap.data()?.email as string,
        action,
        chat,
        senderId
      );
    }
  });
};

export const sendNoticeMailFromClientAgent = (
  mainUid: string,
  member: string[],
  chat: string,
  negotiationSnap: NegotiationType,
  action: string,
  senderId: string
): void => {
  const mUid = mainUid;
  const memberUids = member;
  const idsArray = memberUids;
  idsArray?.push(mUid);
  idsArray?.forEach(async (memberUid) => {
    if (memberUid) {
      const userSnap = await userSnapshot(memberUid);
      await sendPublisherNoticeFromClientAgent(
        negotiationSnap,
        userSnap.data()?.email as string,
        action,
        chat,
        senderId
      );
    }
  });
};

const getEmail = (email: string): string => {
  if (process.env.REACT_APP_ENV === 'prd') {
    return email;
  } else if (process.env.REACT_APP_ENV === 'stg') {
    return email;
  }
  return email;
};

function convertNewLineToBr(text: string): string {
  return text.replace(/(?:\r\n|\r|\n)/g, '<br>');
}

const getServiceName = (service: string): string => service;
